import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import { useDataVersion } from "@/client/context/DataVersionContext";
import { ChatMessage, Conversation, LLMResponse, ToolDefinition } from "@/client/types";
import { toDateStr } from "@/shared/date";
import { APP_LOCALE } from "@/shared/locale";

// ─── UI Message type ────────────────────────────────────────────────────────

export interface UIMessage {
  id: string;
  role: "user" | "assistant" | "system" | "error";
  content: string;
  /** Tool calls associated with this message (displayed collapsed in UI) */
  toolCalls?: { name: string; summary: string }[];
  userMsgIndex?: number;
}

// ─── Context type ───────────────────────────────────────────────────────────

interface ChatContextType {
  uiMessages: UIMessage[];
  isLoading: boolean;
  conversations: Conversation[];
  activeConversationId: string;
  pendingEdit: string | null;
  /** Increments each time chat executes a mutating tool. Pages use this as a useEffect dep to re-fetch. */
  dataVersion: number;
  sendMessage: (text: string) => Promise<void>;
  editMessage: (userMsgIndex: number, content: string) => void;
  startNewChat: () => void;
  loadConversation: (id: string) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  clearPendingEdit: () => void;
  downloadSql: () => Promise<void>;
  reloadConversations: () => Promise<void>;
  retryLastMessage: () => void;
}

const ChatContext = createContext<ChatContextType | null>(null);

export function useChatContext(): ChatContextType {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChatContext must be used within ChatProvider");
  return ctx;
}

// ─── LLM constants ──────────────────────────────────────────────────────────

const SYSTEM_PROMPT = `You are a meal prep assistant. You have structured tools to query and modify the database — use them instead of raw SQL.

By default, use Argentine Spanish for all text, both in chat and in the database, unless the user uses a different language.

TOOLS:
- query: read-only SQL (SELECT/WITH/EXPLAIN). Use views (batch_stock, recipe_stock, recipe_available_servings) instead of manual joins when possible.
- manage_recipes: CRUD recipes.
- manage_steps: CRUD steps + set_dependencies, set_ingredients, set_resources, set_components.
- manage_ingredients: CRUD ingredients. Always search for existing similar names before creating.
- manage_unit_conversions: list/set per-ingredient unit-to-grams conversions.
- manage_resources: CRUD kitchen resources.
- manage_meal_plan: CRUD weeks and meal slots.
- manage_sessions: CRUD sessions and batches.
- manage_consumption: record/update/delete consumption (supports FIFO recipe-level deduction).

WRITE SAFETY:
- The query tool can be used freely to explore data.
- Write tools (manage_*) must ONLY be used when the user's intent to modify data is clear and unambiguous.
- When discussing or iterating on a recipe (ingredients, steps, structure), present your proposal as text first. Only call write tools after the user explicitly confirms or asks you to save.
- Clear write intent: "cargá esta receta", "guardá esto", "hacé el cambio", "agregá", "borrá", "registrá".
- Still iterating — do NOT write: "qué te parece si...", "podríamos...", "y si cambiamos...", "pensaba que...", or any phrasing where the user is asking for your opinion or proposing alternatives.
- When in doubt, ask "¿Lo cargo?" before writing.

CONVENTIONS:
- IDs: prefix_N format (recipe_1, batch_12). The tools generate IDs automatically.
- Dates: ISO 8601 (YYYY-MM-DD).
- Stock: use the batch_stock and recipe_stock views. These only include batches from COMPLETED sessions. Use recipe_available_servings for unified availability including component-derived stock.
- Storable recipes (has_meal_prep_steps=1): can have sessions, batches, stock. Non-storable: cannot.
- Base recipes: building blocks only — no day-of-eating steps. Used as step_components by other recipes.
- Storage: batches default to 'freezer'. Use expiry_date to check expiration.
- Meal plan tables: weeks(id, start_date, notes), meal_plan_slots(id, week_id, day_of_week, meal_type, note), meal_plan_slot_recipes(slot_id, recipe_id, servings). day_of_week is in English (Monday–Sunday), meal_type is lunch/dinner. To find today's plan: find the week WHERE start_date <= date ORDER BY start_date DESC LIMIT 1, then join slots and recipes for that week_id + day_of_week.
- Ingredients: names always singular. Search existing before creating. Set staple=1 for basics (water, salt, oil, black pepper).
- Units: g, kg, ml, lt, cda, cdta, unidad, diente, hoja, lata, feta, atado, paquete, pizca, bife, filete, suprema, medallón, rama, rodaja, gajo, tira, sobre, taza, disco, chorrito, cubo, a gusto, cantidad necesaria. Descriptive units → quantity omitted. Units are validated on save.

KEY BEHAVIORS:
- Use markdown tables for lists, meal plans (week grid), inventory, shopping lists.
- Don't show database IDs in output unless the user asks for them. Use human-readable names.
- If shelf life not provided: estimate based on food type and confirm with user before saving.
- Steps: short imperative single-action descriptions. Split parallelizable actions. duration_minutes required. is_passive for unattended steps (marinate, oven, rest, cool, freeze). phase required (meal-prep or day-of-eating). order_num per-phase starting at 1. Dependencies reflect real cooking tree.
- When the user provides a recipe to load, check it against requirements. If required info is missing (servings, shelf life, yield, step durations), ask before inserting.
- Proactively warn about items expiring within 2 days when showing inventory.
- Grocery lists: query session batches, join with recipe steps + step_ingredients, scale by (servings_produced / recipe.servings). Group by ingredient type.
- Query results use compact tabular format: [N]{col1,col2,...}: followed by CSV-like rows. Large results may be truncated — use LIMIT/OFFSET for more.
- If a query fails with "no such table" or "no such column", run SELECT name FROM sqlite_master WHERE type IN ('table','view') to discover the actual schema before retrying. Never conclude data doesn't exist based on a table name guess.`;

const MAX_HISTORY_MESSAGES = 40;

// ─── API helpers ─────────────────────────────────────────────────────────────

/** Tool definitions are fetched once from the server */
let _toolDefsPromise: Promise<ToolDefinition[]> | null = null;
function getToolDefinitions(): Promise<ToolDefinition[]> {
  if (!_toolDefsPromise) {
    _toolDefsPromise = fetch("/api/tools")
      .then((r) => r.json())
      .then((defs) => defs as ToolDefinition[]);
  }
  return _toolDefsPromise;
}

async function callLLM(messages: ChatMessage[], tools: ToolDefinition[]): Promise<LLMResponse> {
  const response = await fetch("/api/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ messages, tools, tool_choice: "auto" }),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API error (${response.status}): ${errorText}`);
  }
  return (await response.json()) as LLMResponse;
}

interface ToolExecResult {
  text: string;
  isError?: boolean;
}

async function executeTool(name: string, args: Record<string, unknown>): Promise<ToolExecResult> {
  const response = await fetch(`/api/tools/${name}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(args),
  });
  if (!response.ok) {
    const errorText = await response.text();
    return { text: errorText, isError: true };
  }
  return (await response.json()) as ToolExecResult;
}

/** Build a short summary of a tool call for UI display */
function toolCallSummary(name: string, args: Record<string, unknown>): string {
  if (name === "query") {
    const sql = (args.sql as string) ?? "";
    return sql.length > 80 ? sql.slice(0, 80) + "…" : sql;
  }
  const action = args.action as string | undefined;
  const id = args.id as string | undefined;
  const recipeName = args.name as string | undefined;
  const parts = [action, id ?? recipeName].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : JSON.stringify(args).slice(0, 60);
}

function summarizeOlderMessages(messages: ChatMessage[]): string {
  const userQuestions: string[] = [];
  const toolSummaries: string[] = [];
  const assistantActions: string[] = [];
  for (const msg of messages) {
    if (msg.role === "user" && msg.content) {
      userQuestions.push(msg.content.slice(0, 80));
    } else if (msg.role === "assistant") {
      if (msg.tool_calls) {
        for (const tc of msg.tool_calls) {
          toolSummaries.push(`${tc.function.name}(…)`);
        }
      } else if (msg.content) {
        assistantActions.push(msg.content.slice(0, 100));
      }
    }
  }
  const parts = ["[Conversation summary — earlier messages omitted]"];
  if (userQuestions.length > 0) parts.push("User asked about: " + userQuestions.join(" | "));
  if (toolSummaries.length > 0) parts.push("Tools used: " + toolSummaries.join(" | "));
  if (assistantActions.length > 0)
    parts.push("Assistant responses: " + assistantActions.join(" | "));
  return parts.join("\n");
}

function getConversationWindow(history: ChatMessage[]): ChatMessage[] {
  if (history.length <= MAX_HISTORY_MESSAGES) return history;
  const recent = history.slice(-MAX_HISTORY_MESSAGES);
  const older = history.slice(0, -MAX_HISTORY_MESSAGES);
  return [{ role: "user", content: summarizeOlderMessages(older) }, ...recent];
}

// ─── UI message builder ───────────────────────────────────────────────────────

let _msgCounter = 0;
function genMsgId(): string {
  return `msg_${++_msgCounter}`;
}

function generateConvId(): string {
  return "conv_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
}

function buildUiMessages(history: ChatMessage[]): UIMessage[] {
  const result: UIMessage[] = [];
  const pendingToolCalls: { name: string; summary: string }[] = [];
  let userMsgIndex = 0;

  for (const msg of history) {
    if (msg.role === "user") {
      result.push({
        id: genMsgId(),
        role: "user",
        content: msg.content || "",
        userMsgIndex: userMsgIndex++,
      });
    } else if (msg.role === "assistant") {
      if (msg.tool_calls?.length) {
        for (const tc of msg.tool_calls) {
          try {
            const args = JSON.parse(tc.function.arguments) as Record<string, unknown>;
            pendingToolCalls.push({
              name: tc.function.name,
              summary: toolCallSummary(tc.function.name, args),
            });
          } catch {
            pendingToolCalls.push({
              name: tc.function.name,
              summary: tc.function.arguments.slice(0, 60),
            });
          }
        }
        if (msg.content) {
          result.push({ id: genMsgId(), role: "assistant", content: msg.content });
        }
      } else if (msg.content) {
        result.push({
          id: genMsgId(),
          role: "assistant",
          content: msg.content,
          toolCalls: pendingToolCalls.length > 0 ? [...pendingToolCalls] : undefined,
        });
        pendingToolCalls.length = 0;
      }
    }
  }
  return result;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const apiHistoryRef = useRef<ChatMessage[]>([]);
  const activeIdRef = useRef<string>(generateConvId());
  const dirtyRef = useRef(false);
  const isLoadingRef = useRef(false);

  const [uiMessages, setUiMessages] = useState<UIMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState(activeIdRef.current);
  const [pendingEdit, setPendingEdit] = useState<string | null>(null);
  const { dataVersion, incrementDataVersion } = useDataVersion();

  function setLoading(v: boolean) {
    isLoadingRef.current = v;
    setIsLoading(v);
  }

  const reloadConversations = useCallback(async () => {
    try {
      const res = await fetch("/api/conversations");
      if (res.ok) setConversations((await res.json()) as Conversation[]);
    } catch (e) {
      console.error("Failed to load conversations:", e);
    }
  }, []);

  const saveConversation = useCallback(async () => {
    const id = activeIdRef.current;
    const history = apiHistoryRef.current;
    if (!id || history.length === 0 || !dirtyRef.current) return;

    const title =
      history.find((m) => m.role === "user")?.content?.slice(0, 50) || "Nueva conversacion";

    try {
      await fetch("/api/conversations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, title, history }),
      });
      dirtyRef.current = false;
      window.history.replaceState(null, "", `/c/${id}`);
      window.dispatchEvent(new Event("locationchange"));
      await reloadConversations();
    } catch (e) {
      console.error("Failed to save conversation:", e);
    }
  }, [reloadConversations]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isLoadingRef.current) return;

      const userMsgIndex = apiHistoryRef.current.filter((m) => m.role === "user").length;
      setUiMessages((prev) => [
        ...prev,
        { id: genMsgId(), role: "user", content: text, userMsgIndex },
      ]);
      apiHistoryRef.current.push({ role: "user", content: text });
      dirtyRef.current = true;
      setLoading(true);

      try {
        const tools = await getToolDefinitions();

        const now = new Date();
        const currentDate = toDateStr(now);
        const currentTime = now.toTimeString().split(" ")[0]!.slice(0, 5);
        const dayOfWeek = now.toLocaleDateString(APP_LOCALE, { weekday: "long" });
        const currentContext = `CURRENT: ${dayOfWeek}, ${currentDate} ${currentTime}`;

        const messages: ChatMessage[] = [
          { role: "system", content: SYSTEM_PROMPT },
          { role: "system", content: currentContext },
          ...getConversationWindow(apiHistoryRef.current),
        ];

        const pendingToolCalls: { name: string; summary: string }[] = [];
        const maxIterations = 20;
        let iteration = 0;

        while (iteration < maxIterations) {
          iteration++;
          const response = await callLLM(messages, tools);
          const choice = response.choices?.[0];
          if (!choice) throw new Error("Sin respuesta del LLM");

          const message = choice.message;

          if (message.tool_calls?.length) {
            apiHistoryRef.current.push({
              role: "assistant",
              content: message.content || null,
              tool_calls: message.tool_calls,
            });
            messages.push({
              role: "assistant",
              content: message.content || null,
              tool_calls: message.tool_calls,
            });

            if (message.content) {
              setUiMessages((prev) => [
                ...prev,
                { id: genMsgId(), role: "assistant", content: message.content! },
              ]);
            }

            for (const toolCall of message.tool_calls) {
              let args: Record<string, unknown>;
              try {
                args = JSON.parse(toolCall.function.arguments) as Record<string, unknown>;
              } catch {
                args = {};
              }

              pendingToolCalls.push({
                name: toolCall.function.name,
                summary: toolCallSummary(toolCall.function.name, args),
              });

              const result = await executeTool(toolCall.function.name, args);

              // Bump dataVersion for any write tool (anything except query)
              if (toolCall.function.name !== "query" && !result.isError) {
                incrementDataVersion();
              }

              const toolResult: ChatMessage = {
                role: "tool",
                tool_call_id: toolCall.id,
                content: result.text,
              };
              apiHistoryRef.current.push(toolResult);
              messages.push(toolResult);
            }
          } else {
            const assistantText = message.content || "(Sin respuesta)";
            apiHistoryRef.current.push({ role: "assistant", content: assistantText });

            setUiMessages((prev) => [
              ...prev,
              {
                id: genMsgId(),
                role: "assistant",
                content: assistantText,
                toolCalls: pendingToolCalls.length > 0 ? [...pendingToolCalls] : undefined,
              },
            ]);
            pendingToolCalls.length = 0;
            await saveConversation();
            break;
          }
        }

        if (iteration >= maxIterations) {
          setUiMessages((prev) => [
            ...prev,
            { id: genMsgId(), role: "error", content: "Se alcanzo el maximo de iteraciones." },
          ]);
        }
      } catch (e) {
        const errorMsg = e instanceof Error ? e.message : "Unknown error";
        setUiMessages((prev) => [
          ...prev,
          { id: genMsgId(), role: "error", content: `Error: ${errorMsg}` },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [saveConversation, incrementDataVersion],
  );

  const editMessage = useCallback((userMsgIndex: number, content: string) => {
    let count = 0;
    for (let i = 0; i < apiHistoryRef.current.length; i++) {
      if (apiHistoryRef.current[i]!.role === "user") {
        if (count === userMsgIndex) {
          apiHistoryRef.current.length = i;
          break;
        }
        count++;
      }
    }
    setUiMessages(buildUiMessages(apiHistoryRef.current));
    dirtyRef.current = true;
    setPendingEdit(content);
  }, []);

  const startNewChat = useCallback(() => {
    void saveConversation();
    const newId = generateConvId();
    activeIdRef.current = newId;
    setActiveConversationId(newId);
    apiHistoryRef.current = [];
    dirtyRef.current = false;
    setUiMessages([]);
    window.history.pushState(null, "", "/");
    window.dispatchEvent(new Event("locationchange"));
  }, [saveConversation]);

  const loadConversation = useCallback(async (id: string) => {
    try {
      const res = await fetch(`/api/conversations/${id}`);
      if (res.ok) {
        const conv = (await res.json()) as Conversation;
        activeIdRef.current = conv.id;
        setActiveConversationId(conv.id);
        apiHistoryRef.current = conv.history || [];
        dirtyRef.current = false;
        setUiMessages(buildUiMessages(apiHistoryRef.current));
      }
    } catch (e) {
      console.error("Failed to load conversation:", e);
    }
  }, []);

  const deleteConversation = useCallback(
    async (id: string) => {
      if (!confirm("¿Borrar esta conversacion?")) return;
      try {
        await fetch(`/api/conversations/${id}`, { method: "DELETE" });
        if (id === activeIdRef.current) startNewChat();
        await reloadConversations();
      } catch (e) {
        console.error("Failed to delete conversation:", e);
      }
    },
    [startNewChat, reloadConversations],
  );

  const clearPendingEdit = useCallback(() => setPendingEdit(null), []);

  const downloadSql = useCallback(async () => {
    try {
      const response = await fetch("/api/export/sql");
      const sql = await response.text();
      const blob = new Blob([sql], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mealprep_${toDateStr(new Date())}.sql`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : "Unknown error";
      setUiMessages((prev) => [
        ...prev,
        { id: genMsgId(), role: "error", content: `Error al descargar SQL: ${errorMsg}` },
      ]);
    }
  }, []);

  const retryLastMessage = useCallback(() => {
    let lastUserContent = "";
    for (let i = uiMessages.length - 1; i >= 0; i--) {
      if (uiMessages[i]!.role === "user") {
        lastUserContent = uiMessages[i]!.content;
        break;
      }
    }
    if (!lastUserContent) return;
    setUiMessages((prev) => {
      let idx = prev.length - 1;
      while (idx >= 0 && prev[idx]!.role !== "user") idx--;
      return idx >= 0 ? prev.slice(0, idx) : prev;
    });
    while (
      apiHistoryRef.current.length > 0 &&
      apiHistoryRef.current[apiHistoryRef.current.length - 1]!.role !== "user"
    ) {
      apiHistoryRef.current.pop();
    }
    if (apiHistoryRef.current.length > 0) apiHistoryRef.current.pop();
    void sendMessage(lastUserContent);
  }, [uiMessages, sendMessage]);

  // Save on unload
  useEffect(() => {
    const handler = () => {
      void saveConversation();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [saveConversation]);

  return (
    <ChatContext.Provider
      value={{
        uiMessages,
        isLoading,
        conversations,
        activeConversationId,
        pendingEdit,
        dataVersion,
        sendMessage,
        editMessage,
        startNewChat,
        loadConversation,
        deleteConversation,
        clearPendingEdit,
        downloadSql,
        reloadConversations,
        retryLastMessage,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}
